/**
 * The heart on a product.
 *
 * Mirrors FavouriteVendorButton in behaviour so the two feel like one gesture, but keyed by
 * (vertical, product_id): food and pharmacy products live in separate tables with independent
 * id sequences, so an id alone does not identify a product.
 *
 * Filled state is derived from the favourites list rather than held locally, so the heart on a
 * shop card and the heart on that product's own page can never disagree.
 */
import { Heart } from 'lucide-react';
import { cn } from '@/lib/utils';
import { use_favourite_products, use_toggle_favourite_product } from '@/features/favourite/hooks';
import type { ShopVertical } from '@/features/shop/types';

export function FavouriteProductButton({
  vertical,
  product_id,
  product_name,
  size = 'md',
  className,
}: {
  vertical: ShopVertical;
  product_id: string;
  product_name?: string | null;
  size?: 'sm' | 'md';
  className?: string;
}) {
  const favourites_q = use_favourite_products();
  const toggle = use_toggle_favourite_product();

  const favourited = (favourites_q.data ?? []).some(
    (f) => f.product_id === product_id && f.vertical === vertical.toUpperCase(),
  );
  const label = `${favourited ? 'Remove' : 'Save'} ${product_name ?? 'this product'} ${
    favourited ? 'from' : 'to'
  } favourites`;
  const box = size === 'sm' ? 'h-8 w-8' : 'h-9 w-9';
  const icon = size === 'sm' ? 'h-4 w-4' : 'h-[18px] w-[18px]';

  return (
    <button
      type="button"
      // Stop the click reaching the card underneath — on the shop grid the whole card is a
      // link to the product page, and saving something is not the same as opening it.
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        toggle.mutate({ vertical, product_id, favourited });
      }}
      disabled={toggle.isPending}
      aria-label={label}
      aria-pressed={favourited}
      title={label}
      data-testid={`favourite-product-${product_id}`}
      data-favourited={favourited}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full border-2 bg-card/90 backdrop-blur-sm transition-colors active:scale-90 disabled:opacity-60',
        box,
        favourited
          ? 'border-primary/40 text-primary'
          : 'border-brand-outline-variant/30 text-muted-foreground hover:border-primary/40 hover:text-primary',
        className,
      )}
    >
      <Heart className={cn(icon, favourited && 'fill-current')} />
    </button>
  );
}
